import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import Flag from "@/components/Flag";
import { LANGS } from "@/i18n";

const DISMISS_KEY = "op_lang_hint_dismissed";

const HINTS = {
  es: { label: "¿Prefieres ver el sitio en español?", accept: "Sí, cambiar a español", dismiss: "No, gracias" },
  en: { label: "Would you like to switch to English?", accept: "Yes, switch to English", dismiss: "No, thanks" },
  it: { label: "Vuoi vedere il sito in italiano?", accept: "Sì, passa all'italiano", dismiss: "No, grazie" },
  pt: { label: "Prefere ver o site em português?", accept: "Sim, mudar para português", dismiss: "Não, obrigado" },
};

/**
 * Detects the browser's preferred language on first visit and, if it differs
 * from the currently rendered language AND we support it, shows a discreet
 * banner offering to switch. Choice is remembered in localStorage so the
 * banner never re-appears for that visitor.
 */
export default function LanguageHintBanner() {
  const { i18n } = useTranslation();
  const [suggest, setSuggest] = useState(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
      const supported = new Set(LANGS.map((l) => l.code));
      const navLangRaw = (navigator.language || navigator.userLanguage || "").toLowerCase();
      const navLang = navLangRaw.split("-")[0];
      if (!supported.has(navLang)) return;
      if (navLang === i18n.language) return;
      setSuggest(navLang);
    } catch { /* localStorage / navigator unavailable */ }
  }, [i18n.language]);

  if (!suggest) return null;

  const hint = HINTS[suggest] || HINTS.en;

  const accept = () => {
    i18n.changeLanguage(suggest);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setSuggest(null);
  };
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setSuggest(null);
  };

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[92vw] max-w-md rounded-2xl border border-white/20 bg-slate-900/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] text-white p-4 flex items-start gap-3"
      data-testid="lang-hint-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shrink-0">
        <Sparkles size={18} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <Flag code={suggest} />
          <span>{hint.label}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            onClick={accept}
            data-testid="lang-hint-accept"
            className="inline-flex items-center gap-1.5 bg-white text-slate-900 hover:bg-slate-100 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
          >
            <Flag code={suggest} /> {hint.accept}
          </button>
          <button
            onClick={dismiss}
            data-testid="lang-hint-dismiss"
            className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white px-3 py-1.5 rounded-full transition-colors"
          >
            {hint.dismiss}
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="text-white/50 hover:text-white shrink-0"
        aria-label="Close"
        data-testid="lang-hint-close"
      >
        <X size={16} />
      </button>
    </div>
  );
}
